<script lang="ts" setup>
  import { onMounted, ref, useTemplateRef } from 'vue';
  import { InputField } from '../../../../shared/ui/input-field';

  defineProps<{ error?: string }>();
  const emit = defineEmits<{
    gameCode: [value: string];
  }>();

  const code = ref('');
  const inputRef = useTemplateRef<HTMLInputElement>('input');

  const keepDigits = (event: Event) => {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '');
    code.value = input.value;
    if (input.validity.valid) emit('gameCode', code.value);
  };

  const reset = () => {
    code.value = '';
    inputRef.value?.focus();
  };

  onMounted(() => inputRef.value?.focus());

  defineExpose({ reset });
</script>

<template>
  <InputField v-slot="field" :error>
    <input
      ref="input"
      v-bind="field"
      :value="code"
      aria-label="Game code"
      enterkeyhint="go"
      inputmode="numeric"
      maxlength="4"
      pattern="\d{4}"
      required
      type="text"
      @input="keepDigits"
    />
  </InputField>
</template>

<style scoped>
  input {
    --digit-gap: 1ch;
    --slot-color: var(--color-border-strong);
    --slot-gutter: 0.5ch;
    --slot-pitch: calc(1ch + var(--digit-gap));
    --slots-width: calc(4 * var(--slot-pitch) - var(--slot-gutter));

    background-color: transparent;
    background-image: repeating-linear-gradient(
      to right,
      var(--slot-color) 0 calc(var(--slot-pitch) - var(--slot-gutter)),
      transparent 0 var(--slot-pitch)
    );
    background-position: calc(var(--slot-gutter) / 2) 100%;
    background-repeat: no-repeat;
    background-size: var(--slots-width) 0.125rem;
    border: none;
    border-radius: 0;
    caret-color: var(--color-primary);
    color: var(--color-text);
    font-family: var(--font-code);
    font-size: var(--font-size-2xl);
    letter-spacing: var(--digit-gap);
    padding: 0.25rem calc(var(--digit-gap) / 2);
    width: calc(4 * var(--slot-pitch) + var(--digit-gap));

    &:focus {
      --slot-color: var(--color-primary);
    }

    &[aria-invalid='true'] {
      --slot-color: var(--color-error);
    }

    &:focus-visible {
      outline: 2px solid var(--color-primary);
      outline-offset: 4px;
    }
  }
</style>
